import { hash } from 'immutable';
import { type Action, combineActions, handleActions } from 'redux-actions';

import { coreActions } from 'app-components/flux/core-actions';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';

import * as actions from './reportConfigurationsActions';
import type { ReportConfigurationProps } from './reportConfigurationsTypes';

const QcsSyncReportConfigurationStoreFactory =
  resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<ReportConfigurationProps>();

const reportConfigurationsReducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.createReportConfiguration>
  | ReturnType<typeof actions.createReportConfigurationSuccess>
  | ReturnType<typeof actions.updateReportConfiguration>
  | ReturnType<typeof actions.updateReportConfigurationSuccess>
  | ReturnType<typeof actions.deleteReportConfiguration>
  | ReturnType<typeof actions.deleteReportConfigurationSuccess>
  | ReturnType<typeof actions.batchReportConfigurations>
  | ReturnType<typeof actions.batchReportConfigurationsSuccess>
  | ReturnType<typeof actions.getReportConfigurations>
  | ReturnType<typeof actions.getReportConfigurationsSuccess>
  | ReturnType<typeof actions.getReportConfigurationsFailure>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.merge({
            name: hash(resource.name).toString(),
          }),
        ),
      ),

    [actions.createReportConfiguration.type]: (
      state,
      { payload: reportConfiguration }: Action<actions.REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      resourceUpserters.upsertResource(state, reportConfiguration),
    [actions.createReportConfigurationSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteReportConfiguration),

    [actions.updateReportConfiguration.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateReportConfiguration),
    [actions.updateReportConfigurationSuccess.type]: (state, { payload: reportConfiguration }) =>
      resourceUpserters.upsertResource(state, reportConfiguration),

    [actions.deleteReportConfiguration.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createReportConfiguration),
    [actions.deleteReportConfigurationSuccess.type]: (state, { payload: reportConfiguration }) =>
      resourceUpserters.upsertResource(state, reportConfiguration),

    [actions.batchReportConfigurations.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchReportConfigurations, false),
    [actions.batchReportConfigurationsSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchReportConfigurations, true),

    [actions.getReportConfigurations.type]: (state) => state.set('isLoading', true),
    [actions.getReportConfigurationsSuccess.type]: (state, { payload: data }) =>
      resourceUpserters.upsertResources(state, data),
    [actions.getReportConfigurationsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    [combineActions(
      actions.createReportConfigurationFailure.type,
      actions.updateReportConfigurationFailure.type,
      actions.deleteReportConfigurationFailure.type,
      actions.batchReportConfigurationsFailure.type,
    ).toString()]: (state: any, { payload: error }: any) => {
      return resourceUpserters.resetResources(state, error);
    },
  },
  QcsSyncReportConfigurationStoreFactory(),
);

export default reportConfigurationsReducer;
